/**
 * API service for Trust Marketplace backend integration
 */

import { config } from '@/src/config';
import { apiService as mockApiService } from './apiMock';
import { apiService as simpleApiService } from './apiSimple';
import { debug } from './debug';

const API_BASE_URL = config.api.baseUrl;

export interface ApiResponse<T> {
  data?: T;
  error?: string;
  success: boolean;
}

export interface User {
  id: number;
  name: string;
  email: string;
  role: string;
  created_at: string;
  is_active: boolean;
  last_login?: string;
}

export interface UserCreate {
  name: string;
  email: string;
  role: string;
}

export interface UserRegister {
  name: string;
  email: string;
  phone_number?: string;
  password: string;
  role?: string;
  confirmPassword?: string;
  termsAccepted?: boolean;
  termsSignature?: string;
  hipaaAccepted?: boolean;
  hipaaSignature?: string;
}

export interface UserLogin {
  email: string;
  password: string;
}

export interface Token {
  access_token: string;
  refresh_token?: string;
  token_type: string;
  user: {
    id: number;
    name: string;
    email: string;
    role: string;
    is_active: boolean;
  };
  expires_in?: number;
}

export interface UserProfile {
  id: number | string;
  name: string;
  email: string;
  phone_number?: string;
  role: string;
  is_active?: boolean;
  email_verified?: boolean;
  created_at: string;
  last_login?: string;
  preferences?: UserPreferences;
}

export interface UserPreferences {
  user_id: number;
  language_code: string;
  timezone: string;
  date_format: string;
  time_format: string;
  theme: string; // 'light', 'dark', or 'system'
  /** TRUS-498. null/undefined = inherit the form template's setting, not "off". */
  form_autosave_enabled?: boolean | null;
  /** TRUS-498. Seconds. null/undefined = inherit the form template's interval. */
  form_autosave_interval_seconds?: number | null;
  created_at: string;
  updated_at: string;
}

export interface UserPreferencesUpdate {
  language_code?: string;
  timezone?: string;
  date_format?: string;
  time_format?: string;
  theme?: string; // 'light', 'dark', or 'system'
  /** TRUS-498. Send an explicit null to clear the override and inherit again. */
  form_autosave_enabled?: boolean | null;
  form_autosave_interval_seconds?: number | null;
}

export interface Referral {
  id: string;
  referral_number?: string;
  first_name: string;
  last_name: string;
  phone_number?: string;
  email: string;
  date_of_birth?: string;
  zip_code: string;
  preferred_language?: string;
  street_address?: string;
  city?: string;
  state?: string;
  country?: string;
  medicaid_id?: string;
  health_plan?: string;
  plan_type?: string;
  provider_name?: string;
  npi_number?: string;
  organization?: string;
  provider_phone_number?: string;
  primary_diagnosis: string;
  risk_score?: number;
  availability?: string;
  urgency_level: string;
  additional_notes?: string;
  prior_authorization_required?: boolean;
  member_consent_obtained?: boolean;
  hipaa_compliant?: boolean;
  status: string;
  created_at: string;
  updated_at?: string;
  created_by?: number;
  sdoh_needs?: string[];
  housing_subcategories?: string[];
  transportation_subcategories?: string[];
  use_matching_engine?: boolean;
  // Matched provider fields (for Capacity Dashboard tracking)
  matched_provider_id?: string;
  matched_provider_name?: string;
  matched_provider_organization?: string;
  matched_at?: string;
  communication_method?: string;
  // Old format compatibility
  needs?: string[];
}

export interface ReferralCreate {
  referral_number?: string;
  first_name: string;
  last_name: string;
  phone_number?: string;
  email: string;
  date_of_birth?: string;
  zip_code: string;
  preferred_language?: string;
  street_address?: string;
  city?: string;
  state?: string;
  country?: string;
  medicaid_id?: string;
  health_plan?: string;
  plan_type?: string;
  provider_name?: string;
  npi_number?: string;
  organization?: string;
  provider_phone_number?: string;
  primary_diagnosis: string;
  risk_score?: number;
  availability?: string;
  urgency_level: string;
  additional_notes?: string;
  sdoh_needs?: string[];
  housing_subcategories?: string[];
  transportation_subcategories?: string[];
  prior_authorization_required?: boolean;
  member_consent_obtained?: boolean;
  hipaa_compliant?: boolean;
  use_matching_engine?: boolean;
  // Old format compatibility
  needs?: string[];
}

export interface CBO {
  id: string;
  name: string;
  contact_email?: string;
  contact_phone?: string;
  address?: string;
  website?: string;
  services: string[];
  zip_codes: string[];
  languages_supported: string[];
  specializations: string[];
  capacity_pct?: number;
  success_rate?: number;
  trust_score?: number;
  rating?: number;
  distance?: number;
}

export interface Feedback {
  id: string;
  referral_id: string;
  cbo_id: string;
  status: string;
  notes?: string;
  created_at: string;
}

export interface FeedbackCreate {
  referral_id: string;
  cbo_id: string;
  status: string;
  notes?: string;
}

export interface Metrics {
  total_referrals: number;
  total_feedback: number;
  success_rate: number;
  acceptance_rate: number;
  rejection_rate: number;
  failure_rate: number;
  top_cbos: Array<{
    cbo_id: string;
    name: string;
    success_rate: number;
  }>;
  avg_response_time_days: number;
}

class ApiService {
  private baseURL: string;

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    try {
      // Get auth token and active CBO ID from localStorage
      const token = localStorage.getItem('auth_token');
      const activeCboId = localStorage.getItem('active_cbo_id');

      const url = `${this.baseURL}${endpoint}`;
      debug.apiRequest(options.method || 'GET', url, { body: options.body ? JSON.parse(options.body as string) : undefined });

      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0',
          ...(token && { 'Authorization': `Bearer ${token}` }),
          ...(activeCboId && { 'X-CBO-ID': activeCboId }),
          ...options.headers,
        },
        cache: 'no-store',
        ...options,
      });

      debug.apiResponse(response.status, response.ok);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        debug.apiError(response.status, errorData);
        return {
          success: false,
          error: errorData.detail || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      const data = await response.json();
      debug.apiResponse(response.status, true, data);
      return {
        success: true,
        data,
      };
    } catch (error) {
      // Fallback to mock data when API is not available
      debug.warn('API', `Request failed for ${endpoint}, using mock data`, error);
      try {
        // Try the simple API service as fallback
        return await simpleApiService.request<T>(endpoint, options);
      } catch (mockError) {
        debug.error('API', 'Simple API service also failed', mockError);
        return {
          success: false,
          error: `API connection failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        };
      }
    }
  }

  private async getMockData<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    try {
      // Map endpoints to mock methods
      switch (endpoint) {
        case '/health':
          return await mockApiService.healthCheck() as ApiResponse<T>;
        case '/api/referrals':
          if (options.method === 'POST') {
            return await mockApiService.createReferral(JSON.parse(options.body as string)) as ApiResponse<T>;
          }
          return await mockApiService.getReferrals() as ApiResponse<T>;
        case '/api/cbos':
          return await mockApiService.getCBOs() as ApiResponse<T>;
        case '/api/metrics':
          return await mockApiService.getMetrics() as ApiResponse<T>;
        case '/api/auth/register':
          return await mockApiService.register(JSON.parse(options.body as string)) as ApiResponse<T>;
        case '/api/auth/login':
          return await mockApiService.login(JSON.parse(options.body as string)) as ApiResponse<T>;
        case '/api/auth/me':
          return await mockApiService.getCurrentUser('mock_token') as ApiResponse<T>;
        default:
          return {
            success: false,
            error: `Mock data not available for ${endpoint}`,
          };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Mock data error',
      };
    }
  }

  // Health check
  async healthCheck() {
    return this.request<{
      status: string;
      api_version: string;
      database: string;
      total_users: number;
      total_cbos: number;
      total_referrals: number;
      total_feedback: number;
    }>('/health');
  }

  // User endpoints
  async getUsers(): Promise<ApiResponse<User[]>> {
    return this.request<User[]>('/api/users');
  }

  async createUser(user: UserCreate): Promise<ApiResponse<User>> {
    return this.request<User>('/api/users', {
      method: 'POST',
      body: JSON.stringify(user),
    });
  }

  // Referral endpoints
  async getReferrals(params?: { program_id?: number; status?: string }): Promise<ApiResponse<Referral[]>> {
    const queryParams = new URLSearchParams();
    if (params?.program_id) queryParams.append('program_id', params.program_id.toString());
    if (params?.status) queryParams.append('status', params.status);

    const url = queryParams.toString() ? `/api/referrals?${queryParams.toString()}` : '/api/referrals';
    return this.request<Referral[]>(url);
  }

  async createReferral(referral: ReferralCreate): Promise<ApiResponse<Referral>> {
    debug.log('API', 'Creating referral', referral);
    const result = await this.request<Referral>('/api/referrals', {
      method: 'POST',
      body: JSON.stringify(referral),
    });
    debug.log('API', 'Referral creation response', { success: result.success });
    return result;
  }

  async getReferral(id: string): Promise<ApiResponse<Referral>> {
    return this.request<Referral>(`/api/referrals/${id}`);
  }

  // CBO endpoints
  async getCBOs(): Promise<ApiResponse<CBO[]>> {
    return this.request<CBO[]>('/api/cbos');
  }

  async getCBO(id: string): Promise<ApiResponse<CBO>> {
    return this.request<CBO>(`/api/cbos/${id}`);
  }

  // Feedback endpoints
  async createFeedback(feedback: FeedbackCreate): Promise<ApiResponse<{
    success: boolean;
    message: string;
    feedback_id?: string;
  }>> {
    return this.request<{
      success: boolean;
      message: string;
      feedback_id?: string;
    }>('/api/feedback', {
      method: 'POST',
      body: JSON.stringify(feedback),
    });
  }

  async getFeedback(referralId: string): Promise<ApiResponse<{
    referral_id: string;
    feedback_count: number;
    feedback: Feedback[];
  }>> {
    return this.request<{
      referral_id: string;
      feedback_count: number;
      feedback: Feedback[];
    }>(`/api/feedback/${referralId}`);
  }

  async getAllFeedback(): Promise<ApiResponse<{
    total_feedback: number;
    feedback: Feedback[];
  }>> {
    return this.request<{
      total_feedback: number;
      feedback: Feedback[];
    }>('/api/feedback');
  }

  // Metrics endpoint
  async getMetrics(): Promise<ApiResponse<Metrics>> {
    return this.request<Metrics>('/api/metrics');
  }

  // Matching endpoint
  async matchReferral(referral: ReferralCreate): Promise<ApiResponse<Array<{
    cbo_id: string;
    name: string;
    score: number;
    services: string[];
    capacity_pct: number;
    success_rate: number;
    trust_score: number;
    distance?: number;
  }>>> {
    return this.request<Array<{
      cbo_id: string;
      name: string;
      score: number;
      services: string[];
      capacity_pct: number;
      success_rate: number;
      trust_score: number;
      distance?: number;
    }>>('/match_referral', {
      method: 'POST',
      body: JSON.stringify(referral),
    });
  }

  // Match referral to provider (manual matching)
  async matchReferralToProvider(
    referralId: string,
    providerData: {
      provider_id: string;
      provider_name: string;
      provider_organization: string;
      communication_method?: string;
    }
  ): Promise<ApiResponse<{
    success: boolean;
    message: string;
    referral: Referral;
  }>> {
    return this.request<{
      success: boolean;
      message: string;
      referral: Referral;
    }>(`/api/referrals/${referralId}/match`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
      body: JSON.stringify(providerData),
    });
  }

  async deleteReferral(referralId: string): Promise<ApiResponse<{ message: string }>> {
    return this.request<{ message: string }>(`/api/referrals/${referralId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
    });
  }

  // Auto-match referral to multiple providers
  async autoMatchReferral(
    referralId: string,
    matchedProviders: Array<{
      provider_id: string;
      provider_name: string;
      organization_name: string;
      distance_miles: number;
      services: string[];
      status: string;
    }>
  ): Promise<ApiResponse<{
    success: boolean;
    message: string;
    referral: Referral;
  }>> {
    return this.request<{
      success: boolean;
      message: string;
      referral: Referral;
    }>(`/api/referrals/${referralId}/auto-match`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
      body: JSON.stringify({ matched_providers: matchedProviders }),
    });
  }

  // Accept auto-matched referral (exclusive acceptance)
  async acceptAutoMatchedReferral(
    referralId: string,
    providerId: string
  ): Promise<ApiResponse<{
    success: boolean;
    message: string;
    referral: Referral;
  }>> {
    return this.request<{
      success: boolean;
      message: string;
      referral: Referral;
    }>(`/api/referrals/${referralId}/accept`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
      body: JSON.stringify({ provider_id: providerId }),
    });
  }

  // Authentication endpoints
  async register(userData: UserRegister): Promise<ApiResponse<UserProfile>> {
    // Auth endpoints are on WeaveIn API at /api/auth prefix
    try {
      const url = `${config.weaveinApi.baseUrl}/api/auth/register`;
      debug.apiRequest('POST', url);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      debug.apiResponse(response.status, response.ok);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        debug.apiError(response.status, errorData);
        return {
          success: false,
          error: errorData.detail || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      const data = await response.json();
      debug.apiResponse(response.status, true, data);
      return {
        success: true,
        data,
      };
    } catch (error) {
      debug.error('Auth', 'Register API error', error);
      return {
        success: false,
        error: `Registration failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
      };
    }
  }

  async login(loginData: UserLogin): Promise<ApiResponse<Token>> {
    // Auth endpoints are on WeaveIn API, not Trust Marketplace API
    try {
      const url = `${config.weaveinApi.baseUrl}/api/auth/login`;
      debug.apiRequest('POST', url);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(loginData),
      });

      debug.apiResponse(response.status, response.ok);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        debug.apiError(response.status, errorData);
        return {
          success: false,
          error: errorData.detail || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      const data = await response.json();
      // Don't log token data even in dev - just log success
      debug.log('Auth', 'Login successful', { user_id: data.user?.id });
      return {
        success: true,
        data,
      };
    } catch (error) {
      debug.error('Auth', 'Login API error', error);
      return {
        success: false,
        error: `Login failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
      };
    }
  }

  async getCurrentUser(token: string): Promise<ApiResponse<UserProfile>> {
    // Product-aware /me endpoint:
    //   - Triveny  → /api/platform/auth/me  (validates platform JWT)
    //   - WeaveIn  → /api/auth/me           (validates healthcare JWT)
    // The healthcare /me explicitly rejects platform JWTs (token_type guard
    // in app/auth.py), so calling it on Triveny would return 401 and the
    // AuthContext would treat the user as unauthenticated, looping them
    // back to /login.
    try {
      const meEndpoint = config.isTriveny
        ? '/api/platform/auth/me'
        : '/api/auth/me';
      const url = `${config.weaveinApi.baseUrl}${meEndpoint}`;
      debug.apiRequest('GET', url);

      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      debug.apiResponse(response.status, response.ok);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        debug.apiError(response.status, errorData);
        return {
          success: false,
          error: errorData.detail || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      const data = await response.json();
      // Normalize platform /me response to look like the healthcare
      // UserProfile shape the rest of the app expects. Healthcare /me
      // returns fields the platform schema doesn't carry — fill them with
      // sensible defaults so downstream code doesn't break on null.
      if (config.isTriveny) {
        const normalized: any = {
          ...data,
          id: data.id, // UUID string on platform; healthcare expected number
          name: data.first_name && data.last_name
            ? `${data.first_name} ${data.last_name}`
            : data.email,
          // Healthcare-specific fields default to empty
          cbo_id: data.cbo_id ?? null,
          organizations: data.organizations ?? [],
          // Carry platform-only fields explicitly so the UI can branch on them
          tenant_slug: data.tenant_slug,
          tenant_name: data.tenant_name,
        };
        debug.apiResponse(response.status, true, normalized);
        return { success: true, data: normalized as UserProfile };
      }

      // User data contains PII - debug utility will auto-redact sensitive fields
      debug.apiResponse(response.status, true, data);
      return {
        success: true,
        data,
      };
    } catch (error) {
      debug.error('Auth', 'getCurrentUser API error', error);
      return {
        success: false,
        error: `Failed to get user: ${error instanceof Error ? error.message : 'Unknown error'}`,
      };
    }
  }
}

// Export singleton instance
export const apiService = new ApiService();

// Export individual functions for convenience
export const {
  healthCheck,
  getUsers,
  createUser,
  getReferrals,
  createReferral,
  getReferral,
  getCBOs,
  getCBO,
  createFeedback,
  getFeedback,
  getAllFeedback,
  getMetrics,
  matchReferral,
  matchReferralToProvider,
  autoMatchReferral,
  acceptAutoMatchedReferral,
  register,
  login,
  getCurrentUser,
} = apiService;
